import { Wllama } from '@wllama/wllama'
import { useGenesisState } from '../hooks/useGenesisState'

import type {
  WllamaChatMessage,
  ChatCompletionOptions,
  SamplingConfig,
  CompletionChunk,
  AssetsPathConfig
} from '@wllama/wllama'

// Re-export types consumers need
export type { WllamaChatMessage, ChatCompletionOptions, SamplingConfig, CompletionChunk }

// CDN-hosted WASM binaries (pinned to installed version)
const WASM_PATHS: AssetsPathConfig = {
  'single-thread/wllama.wasm': 'https://cdn.jsdelivr.net/npm/@wllama/wllama@2.3.7/src/single-thread/wllama.wasm',
  'multi-thread/wllama.wasm': 'https://cdn.jsdelivr.net/npm/@wllama/wllama@2.3.7/src/multi-thread/wllama.wasm',
}

// Singleton engine instance
let engine: Wllama | null = null
let initPromise: Promise<Wllama> | null = null

// GGUF model URL — SmolLM2 360M Instruct Q4_K_M (~230MB, fast WASM inference)
const MODEL_URL = import.meta.env.VITE_GGUF_URL
  || 'https://huggingface.co/bartowski/SmolLM2-360M-Instruct-GGUF/resolve/main/SmolLM2-360M-Instruct-Q4_K_M.gguf'

/**
 * Get or create the Wllama engine singleton.
 * First call triggers WASM init + model download.
 * Subsequent calls return the cached engine instantly.
 */
export async function getWllamaEngine(): Promise<Wllama> {
  if (engine) return engine

  if (initPromise) return initPromise

  initPromise = (async () => {
    const store = useGenesisState.getState()
    store.setNeuralLoadStatus('Initializing Tatiana neural core...')
    store.setNeuralLoadProgress(0)

    try {
      // Create Wllama instance using CDN-hosted WASM binaries
      const wllama = new Wllama(WASM_PATHS, {
        suppressNativeLog: true,
        parallelDownloads: 3,
        allowOffline: true
      })

      // Calculate thread count — cap at 4 to leave cores free for Three.js + browser
      const nThreads = Math.min(4, Math.max(1, (navigator.hardwareConcurrency ?? 4) - 2))

      store.setNeuralLoadStatus('[ SYSTEM ]: Fetching SmolLM2-360M Neural Weights...')

      // Load model from URL with progress tracking
      await wllama.loadModelFromUrl(MODEL_URL, {
        n_ctx: 1024,
        n_threads: nThreads,
        useCache: true,
        progressCallback: ({ loaded, total }) => {
          const progress = total > 0 ? Math.round((loaded / total) * 100) : 0
          useGenesisState.getState().setNeuralLoadProgress(progress)
          useGenesisState.getState().setNeuralLoadStatus(
            `[ SYSTEM ]: Loading SmolLM2-360M neural weights... ${progress}%`
          )
        }
      })

      engine = wllama
      useGenesisState.getState().setNeuralLoadProgress(100)
      useGenesisState.getState().setNeuralReady(true)
      useGenesisState.getState().setNeuralLoadStatus('Seraph consciousness online.')

      console.log(`[Wllama] Engine ready — SmolLM2-360M, ${nThreads} threads, WASM-SIMD`)
      return wllama
    } catch (err) {
      console.error('[Wllama] Engine init failed:', err)

      // Specific guidance for RangeError (model too large for WASM typed array)
      if (err instanceof RangeError) {
        useGenesisState.getState().setNeuralLoadStatus(
          'Neural synthesis failed — model exceeds browser memory. Enable Hardware Acceleration or use a 64-bit browser.'
        )
      } else {
        useGenesisState.getState().setNeuralLoadStatus(
          'Neural synthesis failed — WASM runtime error'
        )
      }

      initPromise = null
      throw err
    }
  })()

  return initPromise
}

/**
 * Check if the engine is ready (model loaded).
 */
export function isEngineReady(): boolean {
  return engine !== null && engine.isModelLoaded()
}

export default { getWllamaEngine, isEngineReady }
